import {
  CHECKOUT_ROUTE_PATH,
  CHECKOUT_CHANNEL_NAME,
  CHECKOUT_STORAGE_KEY_PREFIX,
  CHECKOUT_SESSION_MAX_AGE_MS,
} from '../constants/bookingConst';

/**
 * Plumbing for the checkout tab: session ids, the stored booking payload, and
 * the message channel between the booking tab and the checkout tab.
 *
 * Why localStorage AND a channel: a tab opened with window.open gets a copy of
 * sessionStorage only at the moment it opens — before the booking exists — so
 * the payload has to live in localStorage to be readable later (including
 * after a reload of the checkout tab). The channel carries the live events
 * (receipt uploaded, submitted, cancelled) and also hands the payload over on
 * request, for browsers where localStorage is blocked.
 */

const storageKey = (sessionId) => `${CHECKOUT_STORAGE_KEY_PREFIX}${sessionId}`;
const messageKey = (sessionId) => `${CHECKOUT_STORAGE_KEY_PREFIX}${sessionId}:message`;

/**
 * Creates a random id for one checkout session.
 * @returns {string}
 */
export const createCheckoutSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Absolute URL of the checkout tab for a session.
 * @param {string} sessionId
 * @param {string} [routePath] - which checkout page; defaults to the court-booking one.
 * @returns {string}
 */
export const buildCheckoutUrl = (sessionId, routePath = CHECKOUT_ROUTE_PATH) =>
  `${window.location.origin}${routePath}/${encodeURIComponent(sessionId)}`;

/**
 * Stores a session payload. Returns false when storage is unavailable
 * (private mode, blocked site data) — the channel handover still works then.
 * @param {string} sessionId
 * @param {Object} session - { state, bookingData?, bookingKeys? }
 * @returns {boolean}
 */
export const saveCheckoutSession = (sessionId, session) => {
  if (!sessionId) return false;
  try {
    window.localStorage.setItem(storageKey(sessionId), JSON.stringify({ ...session, savedAt: Date.now() }));
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Reads a session payload.
 * @param {string} sessionId
 * @returns {Object|null} the stored session, or null if missing/unreadable.
 */
export const readCheckoutSession = (sessionId) => {
  if (!sessionId) return null;
  try {
    const raw = window.localStorage.getItem(storageKey(sessionId));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Removes a session payload once the checkout has ended.
 * @param {string} sessionId
 * @returns {void}
 */
export const clearCheckoutSession = (sessionId) => {
  if (!sessionId) return;
  try {
    window.localStorage.removeItem(storageKey(sessionId));
    window.localStorage.removeItem(messageKey(sessionId));
  } catch (error) {
    // Storage unavailable — nothing was stored, so nothing to clear.
  }
};

/**
 * Drops sessions older than CHECKOUT_SESSION_MAX_AGE_MS, left behind when a
 * tab crashed or the browser was closed mid-checkout.
 * @returns {void}
 */
export const pruneStaleCheckoutSessions = () => {
  try {
    const now = Date.now();
    const staleKeys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(CHECKOUT_STORAGE_KEY_PREFIX) || key.endsWith(':message')) continue;
      let savedAt = 0;
      try { savedAt = Number(JSON.parse(window.localStorage.getItem(key))?.savedAt) || 0; } catch (e) { savedAt = 0; }
      if (now - savedAt > CHECKOUT_SESSION_MAX_AGE_MS) staleKeys.push(key);
    }
    staleKeys.forEach((key) => {
      window.localStorage.removeItem(key);
      window.localStorage.removeItem(`${key}:message`);
    });
  } catch (error) {
    // Storage unavailable — nothing to prune.
  }
};

/**
 * Opens the message channel for a session.
 *
 * Uses BroadcastChannel where available. Older Safari (< 15.4) lacks it, so
 * there the same messages are written to a localStorage key and picked up via
 * the `storage` event, which fires in every other same-origin tab.
 *
 * @param {string} sessionId
 * @param {string} source - CHECKOUT_MESSAGE_SOURCES value for this tab; messages from the same source are ignored.
 * @param {(type: string, payload: any) => void} onMessage
 * @returns {{ post: (type: string, payload?: any) => void, close: () => void }}
 */
export const openCheckoutChannel = (sessionId, source, onMessage) => {
  const deliver = (message) => {
    if (!message || message.source === source) return;
    onMessage(message.type, message.payload);
  };

  if (typeof window.BroadcastChannel === 'function') {
    const channel = new window.BroadcastChannel(`${CHECKOUT_CHANNEL_NAME}:${sessionId}`);
    channel.onmessage = (event) => deliver(event.data);
    let closed = false;
    return {
      post: (type, payload = null) => {
        if (closed) return;
        try {
          channel.postMessage({ type, payload, source });
        } catch (error) {
          console.error('Checkout channel post failed:', error);
        }
      },
      close: () => {
        if (closed) return;
        closed = true;
        channel.close();
      },
    };
  }

  const key = messageKey(sessionId);
  const handleStorage = (event) => {
    if (event.key !== key || !event.newValue) return;
    try {
      deliver(JSON.parse(event.newValue));
    } catch (error) {
      // Malformed entry — ignore it.
    }
  };
  window.addEventListener('storage', handleStorage);
  return {
    post: (type, payload = null) => {
      try {
        // The nonce makes every write a change, so a repeated message still
        // fires a storage event.
        window.localStorage.setItem(key, JSON.stringify({ type, payload, source, nonce: `${Date.now()}-${Math.random()}` }));
      } catch (error) {
        console.error('Checkout channel post failed:', error);
      }
    },
    close: () => window.removeEventListener('storage', handleStorage),
  };
};

/**
 * Opens the checkout tab. Must be called synchronously inside a click
 * handler, or the popup blocker will refuse it.
 *
 * `noopener` is deliberately not used: the booking tab needs the returned
 * handle to notice the tab being closed and to close it itself, and both
 * pages are on the same origin.
 *
 * @param {string} url
 * @returns {Window|null} the tab, or null when the browser blocked it.
 */
export const openCheckoutTab = (url) => {
  try {
    return window.open(url, '_blank') || null;
  } catch (error) {
    return null;
  }
};

/**
 * True when a tab handle is still open.
 * @param {Window|null} tab
 * @returns {boolean}
 */
export const isTabOpen = (tab) => {
  try {
    return !!tab && !tab.closed;
  } catch (error) {
    return false;
  }
};
